import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { suggestUsername, hashPassword, validateRegistrationInput, sanitizeDisplayName, sanitizeEmail, isUsernameAvailable, generateUniqueLinkCode } from '../logic/auth';
import { AccountsState } from '../types';
import { getUniqueSchoolNames } from '../logic/queries';
import { DjayAvatar, DjayBubble, getDjayRegistrationStep } from '../../mascot';
import { RegisterRoleStep, RegisterNameStep, RegisterDetailsStep, RegisterPasswordStep, RegisterSuccessStep } from './register';
/**
 * RegisterScreen — Multi-step registration wizard guided by Djay.
 *
 * Flow: Role → Name → Details → Password → Success (with confetti!)
 *
 * Design principles:
 * - Student-first: Learner is pre-selected and most prominent
 * - Djay as companion: Changes emotion and message per step
 * - Warm palette: Orange/amber matching the brand
 * - Playful progress: Dot indicators instead of a bar
 * - Celebration: Confetti burst on successful registration
 */
interface RegisterScreenProps {
  onRegisterSuccess: (accountId: string, role?: 'learner' | 'parent' | 'teacher' | 'admin') => void;
  onBack: () => void;
  onNavigateToLogin?: () => void;
  accounts?: AccountsState;
  createAccount: (role: 'learner' | 'teacher' | 'parent' | 'admin', details: {
    displayName: string;
    email?: string;
    schoolName?: string;
    grade?: number;
    passwordHash?: string;
    username?: string;
    linkCode?: string;
  }, options?: {
    activate?: boolean;
    cleanupDemo?: boolean;
  }) => string;
}
type Role = 'learner' | 'parent' | 'teacher';
type Step = 'role' | 'name' | 'details' | 'password' | 'success';
const STEPS: Step[] = ['role', 'name', 'details', 'password'];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function RegisterScreen({
  onRegisterSuccess,
  onBack,
  onNavigateToLogin,
  accounts,
  createAccount
}: RegisterScreenProps) {
  const [currentStep, setCurrentStep] = useState<Step>('role');
  const [role, setRole] = useState<Role>('learner');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [grade, setGrade] = useState('6');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [generatedUsername, setGeneratedUsername] = useState('');
  const [generatedLinkCode, setGeneratedLinkCode] = useState('');
  const [schoolInputMode, setSchoolInputMode] = useState<'search' | 'manual'>('search');
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolSelected, setSchoolSelected] = useState(false);
  const [copied, setCopied] = useState(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);
  const existingSchools = useMemo(() => {
    if (!accounts) return [];
    return getUniqueSchoolNames(accounts);
  }, [accounts]);
  const usernameAvailable = useMemo(() => {
    if (role !== 'learner' || !name.trim() || !accounts) return true;
    return isUsernameAvailable(accounts, suggestUsername(name));
  }, [role, name, accounts]);
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setValidationError('');
    if (password !== passwordConfirm) {
      setValidationError('Wachtwoorden komen niet overeen.');
      return;
    }
    if (accounts) {
      const error = validateRegistrationInput(role, {
        name: sanitizeDisplayName(name),
        email: role !== 'learner' ? sanitizeEmail(email) : undefined,
        password,
        schoolName: role === 'teacher' ? schoolName : undefined
      }, accounts);
      if (error) {
        setValidationError(error);
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const passwordHash = await hashPassword(password);
      const sanitizedName = sanitizeDisplayName(name);
      const username = role === 'learner' ? suggestUsername(sanitizedName) : undefined;
      const linkCode = role === 'learner' && accounts ? generateUniqueLinkCode(accounts) : undefined;
      if (username) setGeneratedUsername(username);
      if (linkCode) setGeneratedLinkCode(linkCode);
      const accountId = createAccount(role, {
        displayName: sanitizedName,
        email: role !== 'learner' ? sanitizeEmail(email) : undefined,
        schoolName: role === 'teacher' ? schoolName.trim() : undefined,
        grade: role === 'learner' ? parseInt(grade, 10) : undefined,
        passwordHash,
        username,
        linkCode
      }, {
        activate: true,
        cleanupDemo: true
      });
      setCurrentStep('success');
      successTimeoutRef.current = setTimeout(() => onRegisterSuccess(accountId, role), role === 'learner' ? 6000 : 3500);
    } finally {
      setIsSubmitting(false);
    }
  };
  const nextStep = () => {
    if (currentStep === 'role') setCurrentStep('name');else if (currentStep === 'name') setCurrentStep('details');else if (currentStep === 'details') setCurrentStep('password');
  };
  const prevStep = () => {
    setValidationError('');
    if (currentStep === 'name') setCurrentStep('role');else if (currentStep === 'details') setCurrentStep('name');else if (currentStep === 'password') setCurrentStep('details');else if (currentStep === 'role') onBack();
  };
  const handleSelectRole = (r: Role) => {
    setRole(r);
    setTimeout(nextStep, 250);
  };
  const handleCopyLinkCode = () => {
    navigator.clipboard.writeText(generatedLinkCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const djayState = getDjayRegistrationStep(currentStep, name, role);
  const currentStepIndex = STEPS.indexOf(currentStep as Step);
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50/30 to-white flex items-center justify-center p-4 py-8 sm:py-12 overflow-hidden" data-id="element-663">
      <div className="w-full max-w-md relative z-10" data-id="element-664">
        {/* Back button */}
        {currentStep !== 'success' && <motion.button onClick={prevStep} className="flex items-center gap-2 text-slate-400 hover:text-slate-700 mb-5 transition-colors font-semibold text-sm" initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-665">
            <ArrowLeft className="w-4 h-4" data-id="element-666" /> Terug
          </motion.button>}

        {/* Djay companion */}
        <motion.div className="flex flex-col items-center mb-6" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-667">
          <motion.div animate={{
          y: [0, -8, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-668">
            <DjayAvatar size="lg" emotion={djayState.emotion} animate={true} data-id="element-669" />
          </motion.div>
          <div className="mt-3 w-full max-w-[260px]" data-id="element-670">
            <AnimatePresence mode="wait" data-id="element-671">
              <motion.div key={`${currentStep}-${role}`} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.25
            }} data-id="element-672">
                <DjayBubble message={djayState.text} position="top" autoDismissMs={0} colorScheme="amber" data-id="element-673" />
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Card */}
        <motion.div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden relative" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} data-id="element-674">
          {/* Progress dots */}
          {currentStep !== 'success' && <div className="flex justify-center gap-2 pt-5 pb-1" data-id="element-675">
              {STEPS.map((step, i) => <motion.div key={step} className={`rounded-full transition-all duration-300 ${i <= currentStepIndex ? 'bg-orange-400' : 'bg-slate-200'}`} animate={{
            width: i === currentStepIndex ? 24 : 8,
            height: 8
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} data-id="element-676" />)}
            </div>}

          <div className="p-6 sm:p-8" data-id="element-677">
            <form onSubmit={currentStep === 'password' ? handleRegister : e => {
            e.preventDefault();
            nextStep();
          }} data-id="element-678">
              <AnimatePresence mode="wait" custom={1} data-id="element-679">
                {currentStep === 'role' && <RegisterRoleStep role={role} onSelectRole={handleSelectRole} slideVariants={slideVariants} data-id="element-680" />}

                {currentStep === 'name' && <RegisterNameStep role={role} name={name} onNameChange={setName} usernameAvailable={usernameAvailable} suggestedUsername={suggestUsername(name)} slideVariants={slideVariants} data-id="element-681" />}

                {currentStep === 'details' && <RegisterDetailsStep role={role} grade={grade} onGradeChange={setGrade} email={email} onEmailChange={setEmail} schoolName={schoolName} onSchoolNameChange={setSchoolName} schoolSearch={schoolSearch} onSchoolSearchChange={setSchoolSearch} schoolInputMode={schoolInputMode} onSchoolInputModeChange={setSchoolInputMode} schoolSelected={schoolSelected} onSchoolSelected={setSchoolSelected} existingSchools={existingSchools} onNext={nextStep} slideVariants={slideVariants} data-id="element-682" />}

                {currentStep === 'password' && <RegisterPasswordStep role={role} password={password} passwordConfirm={passwordConfirm} validationError={validationError} isSubmitting={isSubmitting} onPasswordChange={v => {
                setPassword(v);
                setValidationError('');
              }} onPasswordConfirmChange={v => {
                setPasswordConfirm(v);
                setValidationError('');
              }} slideVariants={slideVariants} data-id="element-683" />}

                {currentStep === 'success' && <RegisterSuccessStep role={role} name={name} generatedUsername={generatedUsername} generatedLinkCode={generatedLinkCode} onCopyLinkCode={handleCopyLinkCode} copied={copied} data-id="element-684" />}
              </AnimatePresence>
            </form>
          </div>
        </motion.div>

        {/* Cross-link to Login */}
        {currentStep !== 'success' && onNavigateToLogin && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="mt-6 text-center" data-id="element-685">
            <p className="text-slate-500 font-medium text-sm" data-id="element-686">
              Heb je al een account?{' '}
              <button onClick={onNavigateToLogin} className="text-orange-600 font-bold hover:text-orange-800 hover:underline transition-colors" data-id="element-687">
                Log hier in
              </button>
            </p>
          </motion.div>}
      </div>
    </div>;
}